import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAppLock, biometricAvailable } from "../contexts/AppLockContext";
import { api } from "../lib/api";
import { Lock, Fingerprint, ShieldOff } from "lucide-react";

/**
 * App Lock — Version 1 (launch).
 *
 * Three-choice UX per spec:
 *   Off · PIN only · Device Unlock (biometric with PIN fallback)
 *
 * Fixed 5-minute background timeout. No configurable timeouts, cooldowns
 * or advanced options in v1 — kept intentionally lightweight for launch.
 */
export default function AppLockSettings() {
    const { config, reload, registerBiometric, markUnlocked } = useAppLock();
    const [flow, setFlow] = useState(null); // 'set' | 'change' | 'disable' | null
    const [pin, setPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    const [currentPin, setCurrentPin] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (!config) reload(); }, [config, reload]);

    if (!config) {
        return (
            <div className="text-sm text-[var(--text-ink)]/60" data-testid="app-lock-loading">
                Loading security settings…
            </div>
        );
    }

    // Derive the current "mode" from config for the three-way radio.
    const mode = !config.enabled
        ? "off"
        : (config.biometric_enabled ? "biometric" : "pin");

    const chooseMode = async (next) => {
        if (busy) return;
        if (next === mode) return;

        if (next === "off") {
            if (!config.has_pin) {
                // Nothing to disable — trivially off.
                return;
            }
            setFlow("disable");
            return;
        }

        // Any move away from "off" requires a PIN.
        if (!config.has_pin) {
            setFlow("set");
            setPin(""); setConfirmPin("");
            return;
        }

        setBusy(true);
        try {
            if (next === "pin") {
                await api.put("/security/app-lock/settings", { enabled: true, biometric_enabled: false });
            } else if (next === "biometric") {
                if (!config.has_biometric) {
                    if (!biometricAvailable()) {
                        toast.error("This device does not support biometric unlock");
                        return;
                    }
                    await registerBiometric();
                }
                await api.put("/security/app-lock/settings", { enabled: true, biometric_enabled: true });
            }
            await reload();
            toast.success("App Lock updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update setting");
        } finally { setBusy(false); }
    };

    const savePin = async () => {
        if (!/^\d{4,6}$/.test(pin)) {
            toast.error("PIN must be 4 to 6 digits");
            return;
        }
        if (pin !== confirmPin) {
            toast.error("PINs do not match");
            return;
        }
        setBusy(true);
        try {
            const body = { pin };
            if (flow === "change") {
                if (currentPin) body.current_pin = currentPin;
                if (password) body.password = password;
            }
            await api.post("/security/app-lock/pin", body);
            await reload();
            // Enabling / rotating the PIN implies the user just proved
            // possession right now — treat this as an unlock so the very
            // next page interaction doesn't immediately trigger the lock
            // screen (previously fired on reload because sessionStorage
            // had no prior unlock timestamp).
            markUnlocked();
            toast.success(flow === "change" ? "PIN updated" : "App Lock enabled");
            resetFlow();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save PIN");
        } finally { setBusy(false); }
    };

    const disable = async () => {
        if (!currentPin && !password) {
            toast.error("Enter your PIN or password");
            return;
        }
        setBusy(true);
        try {
            await api.post("/security/app-lock/disable", {
                pin: currentPin || undefined,
                password: password || undefined,
            });
            await reload();
            toast.success("App Lock disabled");
            resetFlow();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not disable App Lock");
        } finally { setBusy(false); }
    };

    const resetFlow = () => {
        setFlow(null);
        setPin(""); setConfirmPin(""); setCurrentPin(""); setPassword("");
    };

    const biometricSupported = biometricAvailable();

    return (
        <div className="space-y-4" data-testid="app-lock-settings">
            <div className="text-xs text-[var(--text-ink)]/60 mb-3">
                Requires unlocking when the app is fully closed and reopened, or after 5 minutes in the background.
                You stay logged in — this is a privacy gate, not a re-authentication.
            </div>

            <div className="grid gap-2 max-w-xl">
                <Choice
                    icon={ShieldOff}
                    active={mode === "off"}
                    onClick={() => chooseMode("off")}
                    disabled={busy}
                    testid="app-lock-choice-off"
                    title="Off"
                    hint="No lock. Anyone with the device can view your workspace."
                />
                <Choice
                    icon={Lock}
                    active={mode === "pin"}
                    onClick={() => chooseMode("pin")}
                    disabled={busy}
                    testid="app-lock-choice-pin"
                    title="PIN only"
                    hint="Enter a 4-6 digit PIN to unlock."
                />
                <Choice
                    icon={Fingerprint}
                    active={mode === "biometric"}
                    onClick={() => chooseMode("biometric")}
                    disabled={busy || !biometricSupported}
                    testid="app-lock-choice-biometric"
                    title={
                        biometricSupported
                            ? "Device Unlock (Face ID / Fingerprint) with PIN fallback"
                            : "Device Unlock — not supported on this device"
                    }
                    hint={
                        biometricSupported
                            ? "Uses your device's built-in biometric. Falls back to PIN."
                            : undefined
                    }
                />
            </div>

            {config.has_pin && flow === null && (
                <div className="pt-3 border-t border-[rgba(26,19,24,0.08)] flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setFlow("change")}
                        disabled={busy}
                        className="text-sm px-3 py-1.5 rounded-lg border border-[rgba(26,19,24,0.12)] hover:border-[rgba(26,19,24,0.3)]"
                        data-testid="app-lock-change-pin-btn"
                    >
                        Change PIN
                    </button>
                </div>
            )}

            {(flow === "set" || flow === "change") && (
                <PinFlow
                    title={flow === "set" ? "Set a 4–6 digit PIN" : "Change PIN"}
                    isChange={flow === "change"}
                    pin={pin} setPin={setPin}
                    confirmPin={confirmPin} setConfirmPin={setConfirmPin}
                    currentPin={currentPin} setCurrentPin={setCurrentPin}
                    password={password} setPassword={setPassword}
                    onSave={savePin} onCancel={resetFlow} busy={busy}
                />
            )}

            {flow === "disable" && (
                <DisableFlow
                    currentPin={currentPin} setCurrentPin={setCurrentPin}
                    password={password} setPassword={setPassword}
                    onDisable={disable} onCancel={resetFlow} busy={busy}
                />
            )}
        </div>
    );
}

function Choice({ icon: Icon, active, onClick, disabled, testid, title, hint }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={active}
            data-testid={testid}
            className={`text-left px-4 py-3 rounded-xl border transition flex items-start gap-3 ${
                active
                    ? "border-[var(--brand-purple)] bg-[var(--brand-purple)]/10"
                    : "border-[rgba(26,19,24,0.12)] hover:border-[rgba(26,19,24,0.3)]"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
            <div className={`mt-0.5 w-8 h-8 rounded-lg grid place-items-center shrink-0 ${
                active ? "brand-gradient text-white" : "bg-[rgba(26,19,24,0.06)]"
            }`}>
                <Icon size={14} />
            </div>
            <div className="flex-1">
                <div className="text-sm font-medium">{title}</div>
                {hint && <div className="text-xs text-[var(--text-ink)]/60 mt-0.5">{hint}</div>}
            </div>
            <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                active ? "border-[var(--brand-purple)] bg-[var(--brand-purple)]" : "border-[rgba(26,19,24,0.25)]"
            }`} />
        </button>
    );
}

function PinFlow({ title, isChange, pin, setPin, confirmPin, setConfirmPin,
                    currentPin, setCurrentPin, password, setPassword,
                    onSave, onCancel, busy }) {
    return (
        <div className="mt-2 p-4 rounded-xl border border-[rgba(26,19,24,0.12)] bg-white/40 space-y-2" data-testid="app-lock-pin-flow">
            <div className="text-sm font-medium mb-1">{title}</div>
            {isChange && (
                <>
                    <PinInput label="Current PIN" value={currentPin} onChange={setCurrentPin} testid="app-lock-current-pin" />
                    <div className="text-xs text-[var(--text-ink)]/50">or</div>
                    <input
                        type="password"
                        placeholder="Account password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-[rgba(26,19,24,0.15)] bg-white/60 text-sm"
                        data-testid="app-lock-password-input"
                    />
                </>
            )}
            <PinInput label="New PIN" value={pin} onChange={setPin} testid="app-lock-new-pin" />
            <PinInput label="Confirm PIN" value={confirmPin} onChange={setConfirmPin} testid="app-lock-confirm-pin" />
            <div className="flex gap-2 pt-1">
                <button type="button" onClick={onSave} disabled={busy}
                        className="px-4 py-2 rounded-lg brand-gradient text-white text-sm disabled:opacity-50"
                        data-testid="app-lock-save-pin-btn">
                    {busy ? "Saving…" : "Save PIN"}
                </button>
                <button type="button" onClick={onCancel}
                        className="px-4 py-2 rounded-lg text-sm border border-[rgba(26,19,24,0.12)]">
                    Cancel
                </button>
            </div>
        </div>
    );
}

function DisableFlow({ currentPin, setCurrentPin, password, setPassword, onDisable, onCancel, busy }) {
    return (
        <div className="mt-2 p-4 rounded-xl border border-red-200 bg-red-50/40 space-y-2" data-testid="app-lock-disable-flow">
            <div className="text-sm font-medium mb-1">Turn off App Lock</div>
            <div className="text-xs text-[var(--text-ink)]/60 mb-2">
                Enter your current PIN or account password to confirm.
            </div>
            <PinInput label="Current PIN" value={currentPin} onChange={setCurrentPin} testid="app-lock-disable-pin" />
            <input type="password" placeholder="Or account password" value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-[rgba(26,19,24,0.15)] bg-white/60 text-sm"
                   data-testid="app-lock-disable-password-input" />
            <div className="flex gap-2 pt-1">
                <button type="button" onClick={onDisable} disabled={busy}
                        className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50"
                        data-testid="app-lock-confirm-disable-btn">
                    {busy ? "…" : "Turn off"}
                </button>
                <button type="button" onClick={onCancel}
                        className="px-4 py-2 rounded-lg text-sm border border-[rgba(26,19,24,0.12)]">
                    Cancel
                </button>
            </div>
        </div>
    );
}

function PinInput({ label, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="text-xs text-[var(--text-ink)]/60">{label}</span>
            <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={value}
                onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[rgba(26,19,24,0.15)] bg-white/60 text-sm tracking-widest font-mono"
                data-testid={testid}
            />
        </label>
    );
}
